import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const NAV = [
  {
    path: "/admin", label: "Overview", exact: true,
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
        <rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/>
      </svg>
    ),
  },
  {
    path: "/admin/products", label: "Products",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/>
        <polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/>
      </svg>
    ),
  },
  {
    path: "/admin/orders", label: "Orders",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
        <line x1="3" y1="6" x2="21" y2="6"/>
        <path d="M16 10a4 4 0 01-8 0"/>
      </svg>
    ),
  },
  {
    path: "/admin/users", label: "Users",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/>
      </svg>
    ),
  },
  {
    path: "/admin/coupons", label: "Coupons",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/>
        <line x1="7" y1="7" x2="7.01" y2="7"/>
      </svg>
    ),
  },
  {
    path: "/admin/sales", label: "Sales",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
      </svg>
    ),
  },
];

export default function AdminLayout({ children }) {
  const loc = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close drawer on navigation
  useEffect(() => { setMobileOpen(false); }, [loc.pathname]);

  // Lock body scroll when mobile drawer is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const isActive = (n) =>
    n.exact ? loc.pathname === n.path : loc.pathname.startsWith(n.path);

  const SidebarContent = () => (
    <>
      {/* Logo */}
      <div style={{
        padding: "24px 20px 18px",
        borderBottom: "1px solid var(--border-subtle)",
        flexShrink: 0,
      }}>
        <div style={{
          fontFamily: "var(--font-display)",
          fontSize: 22,
          letterSpacing: "0.06em",
          color: "var(--accent)",
          lineHeight: 1,
          marginBottom: 5,
        }}>
          TABLECURE
        </div>
        <div style={{
          fontSize: 9,
          color: "var(--text-muted)",
          letterSpacing: "0.22em",
          textTransform: "uppercase",
          fontFamily: "var(--font-mono)",
        }}>
          Admin Console
        </div>
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, padding: "14px 10px", overflowY: "auto" }}>
        {NAV.map((n) => {
          const active = isActive(n);
          return (
            <Link
              key={n.path}
              to={n.path}
              className={`al-nav-link${active ? " active" : ""}`}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "10px 14px",
                borderRadius: "var(--radius-md)",
                marginBottom: 3,
                textDecoration: "none",
                fontSize: 13,
                letterSpacing: "0.02em",
                fontWeight: active ? 500 : 400,
                color: active ? "var(--accent)" : "var(--text-muted)",
                background: active ? "var(--accent-bg)" : "transparent",
                border: `1px solid ${active ? "var(--accent)" : "transparent"}`,
              }}
            >
              <span style={{ flexShrink: 0 }}>{n.icon}</span>
              <span style={{ flex: 1 }}>{n.label}</span>
              {active && (
                <span style={{
                  width: 5, height: 5, borderRadius: "50%",
                  background: "var(--accent)",
                  boxShadow: "0 0 6px var(--accent)",
                  flexShrink: 0,
                }} />
              )}
            </Link>
          );
        })}
      </nav>

      {/* Footer */}
      <div style={{ padding: "14px 10px", borderTop: "1px solid var(--border-subtle)", flexShrink: 0 }}>
        <button
          onClick={() => navigate("/")}
          className="al-back-btn"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "9px 14px",
            borderRadius: "var(--radius-md)",
            background: "transparent",
            border: "1px solid transparent",
            color: "var(--text-muted)",
            fontSize: 12,
            letterSpacing: "0.04em",
            cursor: "pointer",
            width: "100%",
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="12" x2="5" y2="12"/>
            <polyline points="12 19 5 12 12 5"/>
          </svg>
          Back to site
        </button>
      </div>
    </>
  );

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "var(--bg-primary)" }}>
      <style>{`
        @keyframes alSlideIn {
          from { transform: translateX(-100%); }
          to   { transform: translateX(0); }
        }
        @keyframes alFadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }

        /* Nav link hover */
        .al-nav-link:hover:not(.active) {
          background: var(--bg-hover) !important;
          color: var(--text-primary) !important;
          border-color: var(--border-subtle) !important;
        }
        .al-back-btn:hover {
          background: var(--bg-hover) !important;
          color: var(--text-secondary) !important;
        }

        /* Responsive: admin card table scroll */
        .admin-card-wrap {
          overflow: hidden;
          background: var(--bg-card);
          border: 1px solid var(--border-subtle);
          border-radius: var(--radius-lg);
        }

        /* Mobile topbar hidden on desktop */
        .al-topbar { display: none; }

        /* Desktop sidebar */
        .al-sidebar {
          width: 220px;
          min-height: 100vh;
          background: var(--bg-tertiary);
          border-right: 1px solid var(--border-subtle);
          display: flex;
          flex-direction: column;
          position: fixed;
          top: 0; left: 0;
          z-index: 200;
          transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .al-main {
          margin-left: 220px;
          flex: 1;
          padding: 40px 40px 80px;
          min-height: 100vh;
          min-width: 0;
        }

        .al-overlay {
          display: none;
          position: fixed; inset: 0;
          background: rgba(0,0,0,0.65);
          z-index: 190;
          backdrop-filter: blur(3px);
          animation: alFadeIn 0.2s ease;
        }

        /* Tablet: narrower padding */
        @media (max-width: 1024px) {
          .al-main { padding: 32px 24px 64px; }
        }

        /* Mobile */
        @media (max-width: 768px) {
          .al-topbar {
            display: flex !important;
            position: fixed; top: 0; left: 0; right: 0;
            height: 54px;
            background: var(--bg-tertiary);
            border-bottom: 1px solid var(--border-subtle);
            z-index: 150;
            align-items: center;
            padding: 0 16px;
            gap: 12px;
          }
          .al-sidebar {
            transform: translateX(-100%);
            box-shadow: none;
          }
          .al-sidebar.open {
            transform: translateX(0);
            box-shadow: 8px 0 32px rgba(0,0,0,0.4);
            animation: alSlideIn 0.25s cubic-bezier(0.4, 0, 0.2, 1);
          }
          .al-overlay.open {
            display: block !important;
          }
          .al-main {
            margin-left: 0 !important;
            padding: 20px 16px 80px !important;
            padding-top: 72px !important;
          }
          /* Make tables scroll on mobile */
          .admin-card-wrap {
            overflow-x: auto;
          }
          .admin-card-wrap table {
            min-width: 540px;
          }
        }

        @media (max-width: 480px) {
          .al-main {
            padding: 16px 12px 80px !important;
            padding-top: 68px !important;
          }
        }
      `}</style>

      {/* ── Mobile top bar ── */}
      <header className="al-topbar">
        <button
          onClick={() => setMobileOpen(true)}
          style={{
            background: "none", border: "none", cursor: "pointer",
            color: "var(--text-muted)", padding: 6,
            borderRadius: "var(--radius-sm)",
            display: "flex", alignItems: "center",
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
            <line x1="3" y1="6" x2="21" y2="6"/>
            <line x1="3" y1="12" x2="21" y2="12"/>
            <line x1="3" y1="18" x2="21" y2="18"/>
          </svg>
        </button>

        <span style={{
          fontFamily: "var(--font-display)",
          fontSize: 18,
          letterSpacing: "0.08em",
          color: "var(--accent)",
          lineHeight: 1,
        }}>
          TABLECURE
        </span>

        <span style={{
          fontSize: 9,
          color: "var(--text-muted)",
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          fontFamily: "var(--font-mono)",
        }}>
          Admin
        </span>

        <div style={{ flex: 1 }} />

        {/* Current page indicator */}
        <span style={{
          fontSize: 10,
          color: "var(--accent)",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          fontFamily: "var(--font-mono)",
        }}>
          {NAV.find(n => isActive(n))?.label || ""}
        </span>
      </header>

      {/* ── Backdrop overlay ── */}
      <div
        className={`al-overlay${mobileOpen ? " open" : ""}`}
        onClick={() => setMobileOpen(false)}
      />

      {/* ── Sidebar ── */}
      <aside className={`al-sidebar${mobileOpen ? " open" : ""}`}>
        {/* Close button — mobile only */}
        <button
          onClick={() => setMobileOpen(false)}
          style={{
            position: "absolute", top: 14, right: 14,
            background: "none", border: "none", cursor: "pointer",
            color: "var(--text-muted)", padding: 4,
            display: "none",
          }}
          className="al-close-btn"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>

        <SidebarContent />
      </aside>

      {/* ── Main content ── */}
      <main className="al-main">
        {children}
      </main>

      {/* Close button CSS (mobile only) */}
      <style>{`
        @media (max-width: 768px) {
          .al-close-btn { display: block !important; }
        }
      `}</style>
    </div>
  );
}
